import { cn } from '@/lib/utils';
import type { Plumage } from './pigeon-plumage';
import type { Worn } from './pigeon-cosmetics';

/**
 * The product bird.
 *
 * The original traced pigeon SVG was retired in favour of one rendered sparrow
 * in its greeting pose. Keeping this small wrapper means every legacy brand and
 * empty-state callsite receives the same actual bird, rather than a parallel
 * illustration that can drift from the rendered-avatar direction.
 *
 * ARIA: hidden. The bird sits beside a name that is already read out, and
 * announcing "Yusuf's Copper pigeon" on all twenty posts in a feed is noise,
 * not information. It is decoration attached to an identity, and the identity
 * is the text. That holds for what it is *wearing* too, a screen-reader user
 * gaining "wearing a flat cap and a paper kerchief" twenty times down a feed
 * would be worse served, not better.
 */
/**
 * The one path to the brand artwork. Exported because the tab icon and the
 * loading silhouette need the same file, and three string literals is three
 * chances for one of them to keep pointing at a retired bird.
 */
export const BRAND_BIRD_SRC = '/brand-sparrow-greeting.png';

export function Pigeon({
  plumage: _plumage,
  worn: _worn,
  blink: _blink = 0,
  halo: _halo = 0,
  className,
  style,
  ...props
}: Omit<React.ComponentProps<'img'>, 'src' | 'alt' | 'width' | 'height'> & {
  /** Retained for existing callers; the shared brand avatar has one plumage. */
  plumage?: string | Plumage;
  /** Retained for existing callers; the static brand avatar does not blink. */
  blink?: number;
  /** Retained for existing callers; the static brand avatar has no cosmetics. */
  worn?: Worn;
  /** Retained for existing callers; the rendered asset needs no halo. */
  halo?: number;
}) {
  return (
    <img
      data-slot="brand-bird"
      src={BRAND_BIRD_SRC}
      alt=""
      aria-hidden
      /*
        THE ARTWORK'S OWN DIMENSIONS, AND THEY ARE LOAD-BEARING. Every caller
        sizes this by width and lets the height follow, and `BirdPortrait` holds
        its whole box open with this element (see the note there). An `<img>`
        with no intrinsic ratio declared is zero pixels tall until the file
        decodes, which collapsed the byline, the row and the skeleton drawn over
        it, and then reflowed all three when the bird landed. The attributes give
        the box its aspect ratio on the first frame instead.
      */
      /* The largest use is 7.25rem on the public landing page. A 256px source
         preserves a 2x image there while keeping every 20–64px UI bird from
         installing a duplicate copy of the 512px PWA icon. */
      width={256}
      height={256}
      /*
        `bird-keyline` is the hairline the 2D bird used to get from a stroked
        path under its fills (`index.css`). The bird is a PNG now, so it takes
        the same half of that rule the 3D capture does — without it a dark bird
        on a near-black card has no separation from it at all.
      */
      className={cn('bird-keyline h-auto w-full object-contain', className)}
      style={style}
      {...props}
    />
  );
}
